import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarCheck, ChevronRight, Clock } from "lucide-react";
import { useAuth } from "@/core/hooks/useAuth";
import { BlocoData } from "@/modules/escala/components/BlocoData";
import { Pill } from "@/modules/escala/components/Pill";
import {
  abaDaEscala,
  formatarHora,
  marcadorMinhaResposta,
  rotuloDiaLongo,
} from "@/modules/escala/format";
import { useEscalas } from "@/modules/escala/hooks/useEscalas";

const LIMITE_PREVIEW = 2;

/**
 * Preview da Escala na Início: as próximas escalas em que a PESSOA está na
 * equipe (as 2 mais próximas), com dia, hora, ministério, função e a situação
 * da própria resposta (aguardando confirmação ou não). "Ver todos +N" leva à
 * lista do elo. Não mostra escala cancelada, já realizada, em que a pessoa
 * recusou ou em que ela não está na equipe. Sem nada a mostrar (ou enquanto
 * carrega), a seção fica oculta, como nos demais previews. Só aparece se a
 * pessoa enxerga o elo Escala.
 *
 * Reaproveita `useEscalas` (mesma consulta e mesmo cache da lista do elo): a
 * visibilidade e a autorização continuam sendo as da RLS, nada é filtrado
 * por igreja ou ministério aqui.
 */
export function PreviewEscalaHome() {
  const navigate = useNavigate();
  const { pessoa, modulosVisiveis } = useAuth();
  const veElo = modulosVisiveis.includes("escala");

  const { data, isLoading } = useEscalas(veElo ? pessoa?.id : undefined);

  const minhasProximas = useMemo(() => {
    const agora = new Date();
    return (data ?? [])
      .filter(
        (e) =>
          abaDaEscala(e, agora) === "agendadas" &&
          e.minhaParticipacao !== null &&
          e.minhaParticipacao.resposta !== "RECUSADA",
      )
      .sort((a, b) => new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime());
  }, [data]);

  if (!veElo || isLoading || minhasProximas.length === 0) return null;

  const itens = minhasProximas.slice(0, LIMITE_PREVIEW);
  const restantes = minhasProximas.length - itens.length;

  return (
    <section
      aria-labelledby="preview-escala-titulo"
      className="mb-4 rounded-2xl border border-border bg-surface p-4 sm:p-5"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 id="preview-escala-titulo" className="flex items-center gap-2 font-bold text-text">
          <CalendarCheck size={18} className="text-primary" /> Minhas Escalas
        </h2>
      </div>

      <ul className="flex flex-col gap-2">
        {itens.map((e) => {
          const minha = e.minhaParticipacao;
          const resposta = minha
            ? marcadorMinhaResposta(minha.resposta, minha.origemResposta)
            : null;
          return (
            <li key={e.id}>
              <button
                type="button"
                onClick={() => navigate(`/escalas/${e.id}`)}
                className="flex min-h-11 w-full items-center gap-3 rounded-xl border border-border bg-surface p-2.5 text-left transition-colors hover:bg-surface-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <BlocoData iso={e.data_hora} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm font-semibold text-text">{e.nome}</span>
                  <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted">
                    {/* O bloco de data é decorativo (aria-hidden): leitor de tela precisa da data por extenso. */}
                    <span className="sr-only">{rotuloDiaLongo(e.data_hora)},</span>
                    <Clock size={13} aria-hidden="true" className="shrink-0" />
                    <span className="font-semibold text-text">{formatarHora(e.data_hora)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{e.ministerioNome}</span>
                    {minha?.funcaoNome && (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{minha.funcaoNome}</span>
                      </>
                    )}
                  </span>
                  {resposta && (
                    <span className="mt-1">
                      <Pill tom={resposta.tom} icone={resposta.icone}>
                        {resposta.texto}
                      </Pill>
                    </span>
                  )}
                </span>
                <ChevronRight size={16} className="shrink-0 text-muted" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>

      {restantes > 0 && (
        <button
          type="button"
          onClick={() => navigate("/escalas")}
          className="mt-2.5 flex min-h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-primary/10 text-[13px] font-bold text-primary hover:bg-primary/15"
        >
          Ver todos
          <span className="grid h-[22px] min-w-[22px] place-items-center rounded-full bg-primary px-1.5 text-xs text-on-primary">
            +{restantes}
          </span>
        </button>
      )}
    </section>
  );
}
